/**
 * Email-verification landing screen (T27.2.10.1 Phase B, D2/D11).
 *
 * The verify link in the email points at `/verify/:token`. On mount this consumes
 * the token via `useVerifyEmail` (→ POST /api/auth/verify). On success the
 * backend creates the account + issues a session, which this applies (shared
 * `useApplyAuthSuccess`) and navigates to home. On an invalid / expired / reused
 * token the backend returns a 400 and this shows a "link expired — sign up again"
 * message with a path back to Create account.
 *
 * Auto-consumes on mount (single attempt — a verify token is single-use, so a
 * re-fire would 400 the second time). Web + native (native flag forwarded so the
 * issued session returns the refresh token in the body).
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useVerifyEmail } from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import { Card, Logo } from '../../components';
import { type WebTokenStorage } from '../../auth';
import { routes } from '../../routes/paths';
import { isNativePlatform } from '../../routes/nativeGoogleSignIn';
import { useApplyAuthSuccess } from './useApplyAuthSuccess';
import {
  authPageStyle,
  authStackStyle,
  authWordmarkRowStyle,
  authCardBodyStyle,
  authHeadingStyle,
  authSubheadingStyle,
  authInlineNoteStyle,
  authInlineErrorStyle,
  authLinkStyle,
} from './authLayout';

export interface VerifyPageProps {
  tokens: WebTokenStorage;
}

export function VerifyPage({ tokens }: VerifyPageProps): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const verify = useVerifyEmail();
  const applyAuthSuccess = useApplyAuthSuccess(tokens);
  const [error, setError] = useState<string | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return; // single-use token — consume once
    firedRef.current = true;
    if (!token) {
      setError('This verification link is missing its token.');
      return;
    }
    const native = isNativePlatform();
    verify.mutate(
      native ? { token, native: true } : { token },
      {
        onSuccess: applyAuthSuccess,
        onError: (err) =>
          setError(
            apiErrorMessage(err, 'This verification link is invalid or has expired.'),
          ),
      },
    );
    // Fire exactly once on mount; deps intentionally omitted (the ref guards it).
  }, []);

  return (
    <main style={authPageStyle} data-testid="verify-page">
      <div style={authStackStyle}>
        <div style={authWordmarkRowStyle}>
          <Logo size={80} showText data-testid="verify-logo" />
        </div>
        <Card padding="lg" shadow="md" data-testid="verify-card">
          <div style={authCardBodyStyle}>
            {error ? (
              <>
                <h1 style={authHeadingStyle}>Link expired</h1>
                <p
                  role="alert"
                  style={authInlineErrorStyle}
                  data-testid="verify-error"
                >
                  {error}
                </p>
                <p style={authInlineNoteStyle}>
                  Verification links work for 24 hours and can be used once.{' '}
                  <Link to={routes.register} style={authLinkStyle} data-testid="verify-retry">
                    Sign up again
                  </Link>{' '}
                  to get a fresh one.
                </p>
              </>
            ) : (
              <>
                <h1 style={authHeadingStyle}>Confirming your email…</h1>
                <p style={authSubheadingStyle} data-testid="verify-pending">
                  Hang tight — finishing setting up your account.
                </p>
              </>
            )}
          </div>
        </Card>
      </div>
    </main>
  );
}
